import { useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';

export const ScriptInjector = () => {
  const { user } = useAuth();
  const injectedNodesRef = useRef<Node[]>([]);

  useEffect(() => {
    const processNode = (node: Node): Node => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        
        if (element.tagName.toLowerCase() === 'script') {
          const scriptElement = element as HTMLScriptElement;
          const newScript = document.createElement('script');

          Array.from(scriptElement.attributes).forEach(attr => {
            newScript.setAttribute(attr.name, attr.value);
          });

          if (scriptElement.src) {
            newScript.src = scriptElement.src;
          }

          if (scriptElement.textContent) {
            newScript.textContent = scriptElement.textContent;
          }

          return newScript;
        } else {
          const clonedElement = element.cloneNode(false) as Element;
          
          Array.from(element.childNodes).forEach(child => {
            const processedChild = processNode(child);
            clonedElement.appendChild(processedChild);
          });
          
          return clonedElement;
        }
      } else {
        return node.cloneNode(false);
      }
    };

    const loadCustomScript = async () => {
      if (!user || !user.tenantId) {
        return;
      }

      // Skip external script if local Gemini agent is active (takes priority)
      if ((window as any).__LOCAL_AGENT_ACTIVE__ === true) {
        return;
      }

      try {
        // Inyectar datos del usuario en window.__USER_DATA__ antes de cargar scripts
        (window as any).__USER_DATA__ = {
          userId: user.id,
          userName: user.fullName,
          userEmail: user.email,
          userRolName: user.role,
          tenantId: user.tenantId,
        };

        const response = await fetch('/api/company/script', {
          credentials: 'include',
        });

        if (!response.ok) {
          console.warn('No se pudo cargar el script personalizado');
          return;
        }

        const company = await response.json();

        if (company.customScript && company.customScript.trim()) {
          const tempContainer = document.createElement('div');
          tempContainer.innerHTML = company.customScript.trim();

          const fragment = document.createDocumentFragment();

          Array.from(tempContainer.childNodes).forEach((node) => {
            const processedNode = processNode(node);
            fragment.appendChild(processedNode);
            injectedNodesRef.current.push(processedNode);
          });

          document.body.appendChild(fragment);
        }
      } catch (error) {
        console.error('Error cargando script personalizado:', error);
      }
    };

    loadCustomScript();

    return () => {
      injectedNodesRef.current.forEach(node => {
        if (node.parentNode) {
          node.parentNode.removeChild(node);
        }
      });
      injectedNodesRef.current = [];

      const geminiButton = document.getElementById('gemini-chat-button');
      const geminiIframe = document.getElementById('gemini-chat-iframe');
      if (geminiButton) geminiButton.remove();
      if (geminiIframe) geminiIframe.remove();
    };
  }, [user]);

  return null;
};
